import styled from 'styled-components';

export const ScaleRoot = styled.div`
  --scale-accent-fallback: #4f46e5;
  --scale-color: var(--scale-accent, var(--scale-accent-fallback));
  width: 100%;
  min-width: 0;
  margin: 8px 0;
  font-family: inherit;
  color: inherit;

  && .scale-option {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
    margin: 0;
    padding: 0;
    border: none;
    background: transparent;
    box-shadow: none;
    color: inherit;
    font-family: inherit;
    cursor: pointer;
    transition: transform 0.15s ease, background-color 0.15s ease, border-color 0.15s ease,
      color 0.15s ease, box-shadow 0.15s ease;
  }

  && .scale-option:disabled {
    cursor: default;
  }

  && .scale-option:focus-visible {
    outline: 2px solid var(--scale-color);
    outline-offset: 2px;
  }

  .scale-labels {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-top: 10px;
    font-size: 13px;
    line-height: 1.35;
    opacity: 0.7;
  }

  .scale-labels span:nth-child(2) {
    text-align: center;
  }

  .scale-labels span:last-child {
    text-align: right;
  }

  .scale-error-text {
    margin-top: 6px;
    color: #d32f2f;
    font-size: 0.75rem;
  }

  .rating-list {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  && .rating-point {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    color: #d1d5db;
  }

  && .rating-point svg {
    width: 34px;
    height: 34px;
  }

  && .rating-point.is-star.is-active {
    color: #f59e0b;
  }

  && .rating-point.is-heart.is-active {
    color: #ef4444;
  }

  && .rating-point.is-emoji {
    font-size: 30px;
    line-height: 1;
    filter: grayscale(1);
    opacity: 0.45;
  }

  && .rating-point.is-emoji.is-active {
    filter: none;
    opacity: 1;
  }

  && .rating-point.is-emoji.is-selected {
    transform: scale(1.18);
  }

  &.is-live .rating-point:not(:disabled):hover {
    transform: scale(1.1);
  }

  && .opinion-point {
    height: 46px;
    border: 1.5px solid #e5e7eb;
    border-radius: 10px;
    background: #ffffff;
    color: #374151;
    font-size: 15px;
    font-weight: 600;
  }

  &.is-live .opinion-point:not(:disabled):not(.is-selected):hover {
    border-color: var(--scale-color);
    transform: translateY(-1px);
  }

  && .opinion-point.is-selected {
    border-color: var(--scale-color);
    background: var(--scale-color);
    color: #ffffff;
  }

  && .opinion-point.tone-low {
    border-color: #fecaca;
  }

  && .opinion-point.tone-mid {
    border-color: #fde68a;
  }

  && .opinion-point.tone-high {
    border-color: #bbf7d0;
  }

  && .opinion-point.tone-low.is-selected {
    border-color: #ef4444;
    background: #ef4444;
  }

  && .opinion-point.tone-mid.is-selected {
    border-color: #f59e0b;
    background: #f59e0b;
  }

  && .opinion-point.tone-high.is-selected {
    border-color: #22c55e;
    background: #22c55e;
  }

  .slider-field {
    position: relative;
    padding: 44px 0 4px;
  }

  &&& .slider-input {
    display: block;
    width: 100% !important;
    height: 6px !important;
    min-height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    border: none !important;
    border-radius: 999px !important;
    background: linear-gradient(
        to right,
        var(--scale-color) 0%,
        var(--scale-color) var(--fill),
        transparent var(--fill)
      ),
      color-mix(in srgb, var(--scale-color) 14%, #eef0f3) !important;
    box-shadow: none !important;
    outline: none;
    appearance: none;
    -webkit-appearance: none;
    cursor: pointer;
  }

  &&& .slider-input.is-placeholder {
    background: #eef0f3 !important;
  }

  &&& .slider-input:disabled {
    cursor: default;
  }

  &&& .slider-input::-webkit-slider-thumb {
    width: 22px;
    height: 22px;
    border: 3px solid var(--scale-color);
    border-radius: 50%;
    background: #ffffff;
    box-shadow: 0 2px 6px rgba(15, 23, 42, 0.18);
    -webkit-appearance: none;
    appearance: none;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
  }

  &&& .slider-input::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border: 3px solid var(--scale-color);
    border-radius: 50%;
    background: #ffffff;
    box-shadow: 0 2px 6px rgba(15, 23, 42, 0.18);
    transition: transform 0.15s ease, box-shadow 0.15s ease;
  }

  &.is-live .slider-input:not(:disabled):hover::-webkit-slider-thumb,
  &.is-live .slider-input:not(:disabled):focus-visible::-webkit-slider-thumb {
    box-shadow: 0 0 0 7px color-mix(in srgb, var(--scale-color) 16%, transparent),
      0 2px 6px rgba(15, 23, 42, 0.18);
  }

  &.is-live .slider-input:not(:disabled):active::-webkit-slider-thumb {
    transform: scale(1.12);
  }

  &.is-live .slider-input:not(:disabled):hover::-moz-range-thumb,
  &.is-live .slider-input:not(:disabled):focus-visible::-moz-range-thumb {
    box-shadow: 0 0 0 7px color-mix(in srgb, var(--scale-color) 16%, transparent),
      0 2px 6px rgba(15, 23, 42, 0.18);
  }

  &&& .slider-input.is-placeholder::-webkit-slider-thumb {
    border-color: #c4c8d0;
  }

  &&& .slider-input.is-placeholder::-moz-range-thumb {
    border-color: #c4c8d0;
  }

  .slider-bubble {
    position: absolute;
    top: 0;
    padding: 5px 10px;
    border-radius: 8px;
    background: var(--scale-color);
    color: #ffffff;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;
    transform: translateX(-50%);
    pointer-events: none;
    box-shadow: 0 4px 12px color-mix(in srgb, var(--scale-color) 30%, transparent);
  }

  .slider-bubble::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: -4px;
    width: 8px;
    height: 8px;
    background: inherit;
    transform: translateX(-50%) rotate(45deg);
  }

  .slider-bubble.is-placeholder {
    background: #f3f4f6;
    color: #6b7280;
    box-shadow: none;
  }

  &.has-error .opinion-point:not(.is-selected) {
    border-color: #d32f2f;
  }

  @container (max-width: 420px) {
    && .opinion-point {
      height: 40px;
      font-size: 13px;
    }

    && .rating-point {
      width: 38px;
      height: 38px;
    }
  }
`;

export const OpinionList = styled.div<{ $count: number }>`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$count}, minmax(0, 1fr));
  gap: 6px;
`;

export const SliderRange = styled.input<{ $fill: number }>`
  --fill: ${(p) => p.$fill}%;
`;

const SLIDER_THUMB_PX = 22;

export const SliderBubble = styled.div<{ $fill: number }>`
  left: calc(
    ${(p) => p.$fill}% + ${(p) => SLIDER_THUMB_PX / 2 - (p.$fill / 100) * SLIDER_THUMB_PX}px
  );
`;
